export type GoogleLinkState = {
  phase: 'checking' | 'ready' | 'code' | 'choose' | 'linked' | 'unknown';
  busy: boolean;
  phoneHint: string;
  error: boolean;
};
type Dependencies = {
  getGoogleSignInStatus: (session: string) => Promise<{ linked: boolean; linkedAt: string | null }>;
  startGoogleReauthentication: (session: string) => Promise<{ attempt: string; expiresAt: string; phoneHint: string }>;
  completeGoogleReauthentication: (session: string, attempt: string, code: string) => Promise<{ token: string }>;
  link: (proof: string, signal: AbortSignal) => Promise<{ linked: true } | null>;
};
export const initialGoogleLinkState: GoogleLinkState = { phase: 'checking', busy: false, phoneHint: '', error: false };

// Credentials stay in this screen-owned closure, never storage or app session.
export function createGoogleLinkController(session: string, api: Dependencies, changed: (state: GoogleLinkState) => void) {
  let state = initialGoogleLinkState;
  let attempt: string | undefined;
  let proof: string | undefined;
  let closed = false;
  const lifetime = new AbortController();
  const publish = (patch: Partial<GoogleLinkState>) => {
    if (closed) return;
    state = { ...state, ...patch };
    changed(state);
  };
  const clear = () => { attempt = undefined; proof = undefined; };
  const status = async (error = false) => {
    const result = await api.getGoogleSignInStatus(session);
    if (!result || typeof result.linked !== 'boolean') throw new Error('invalid_status');
    publish({ phase: result.linked ? 'linked' : 'ready', phoneHint: '', error: !result.linked && error });
  };
  async function run(phases: GoogleLinkState['phase'][], work: () => Promise<void>) {
    if (closed || state.busy || !phases.includes(state.phase)) return;
    publish({ busy: true, error: false });
    try { await work(); } finally { publish({ busy: false }); }
  }
  return {
    refresh: () => run(['checking', 'unknown', 'ready'], async () => {
      clear();
      try { await status(); } catch { publish({ phase: 'unknown', error: true }); }
    }),
    send: () => run(['ready'], async () => {
      clear();
      try {
        const result = await api.startGoogleReauthentication(session);
        if (closed) return;
        if (!result || !/^[A-Za-z0-9_-]{43}$/.test(result.attempt)
          || !Number.isFinite(Date.parse(result.expiresAt)) || typeof result.phoneHint !== 'string'
          || !/^•••• \d{2}$/.test(result.phoneHint)) throw new Error('invalid_attempt');
        attempt = result.attempt;
        publish({ phase: 'code', phoneHint: result.phoneHint });
      } catch { publish({ phase: 'ready', error: true }); }
    }),
    verify: (code: string) => run(['code'], async () => {
      if (!/^\d{4,10}$/.test(code) || !attempt) { publish({ error: true }); return; }
      const current = attempt;
      attempt = undefined; // one check, including network/server failure
      try {
        const result = await api.completeGoogleReauthentication(session, current, code);
        if (closed) return;
        if (!result || typeof result.token !== 'string' || !result.token || result.token.length > 16_384) throw new Error('invalid_proof');
        proof = result.token;
        publish({ phase: 'choose', phoneHint: '' });
      } catch { clear(); publish({ phase: 'ready', phoneHint: '', error: true }); }
    }),
    choose: () => run(['choose'], async () => {
      if (!proof) { publish({ phase: 'ready', error: true }); return; }
      try {
        const result = await api.link(proof, lifetime.signal);
        if (closed || result === null) return; // explicit cancellation can retry while proof is valid
        clear();
        // Reconcile even after success. The status endpoint is authoritative.
        try { await status(true); } catch { publish({ phase: 'unknown', error: true }); }
      } catch {
        clear();
        if (closed) return;
        // A POST may have succeeded before its response was lost. Never show a
        // false disconnected state or automatically send another SMS/link POST.
        try { await status(true); } catch { publish({ phase: 'unknown', error: true }); }
      }
    }),
    restart: () => { if (!closed && !state.busy && state.phase === 'code') { clear(); publish({ phase: 'ready', phoneHint: '', error: false }); } },
    dispose: () => { closed = true; clear(); lifetime.abort(); },
  };
}
